import React, { FunctionComponent, useState, useEffect } from 'react';
import { Modal, Form, Row, Col, Input, Typography, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { Store } from 'antd/es/form/interface';
import { Gutter } from 'antd/es/grid/row';
import { Amenity as AmenityIcon } from 'Components/icons';
import { Close } from 'Components/icons';
import { ValidatedFormItem } from 'Components/validated-form-item';
import { CreateAmenityCategorySchema } from 'Schemas';
import { useStores, useFormValidation, useService } from 'Hooks';
import './amenity-category-modal.less';
import { ImagePicker } from 'Components/image-picker';
import { UpdateFileRequestDto } from 'Api/Features/General/Dtos/UpdateFileRequestDto';
import { AmenityCategoryDto } from 'Api/Features/AmenityCategories/Dtos/AmenityCategoryDto';
import { AmenityCategoryService } from 'Services/AmenityCategoryService';
import { CreateAmenityCategoryRequestDto } from 'Api/Features/AmenityCategories/Dtos/CreateAmenityCategoryRequestDto';
import { UpdateAmenityCategoryRequestDto } from 'Api/Features/AmenityCategories/Dtos/UpdateAmenityCategoryRequestDto';
import { UploadFile } from 'antd/lib/upload/interface';
import { ImageDetails } from 'Components/image-picker/image-picker';

const { Title } = Typography;

const titleGutter: [Gutter, Gutter] = [0, 0];
const formGutter: [Gutter, Gutter] = [40, 0];

interface AmenityCategoryModalProps {
    visible: boolean;
    onComplete: (success: boolean) => void;
    amenityCategory?: AmenityCategoryDto;
}

const AmenityCategoryModal: FunctionComponent<AmenityCategoryModalProps> = ({
    visible,
    onComplete,
    amenityCategory,
}) => {
    const { t } = useTranslation();
    const { globalLoadingStore, toastStore, confirmationModalStore } = useStores();
    const amenityCategoryService = useService(AmenityCategoryService);
    const [form] = Form.useForm();
    const [errors, validateForm, resetErrors, setErrors] = useFormValidation(
        CreateAmenityCategorySchema, form
    );
    const [mainImagesDetails, setMainImagesDetails] = useState<ImageDetails[]>();

    const dismiss = (success = false): void => {
        onComplete(success);
        form.resetFields();
        resetErrors();
    };

    const success = async (): Promise<void> => {
        if (
            !(await confirmationModalStore.confirm({
                icon: <AmenityIcon />,
                title: t(`Amenities.amenity_category_saved_success`),
                message: t(`Amenities.amenity_category_successfully_saved`),
                positiveText: t('ok'),
            }))
        )
            return;
        dismiss(true);
    };

    useEffect(() => {
        if (amenityCategory) {
            form.setFieldsValue({
                name: amenityCategory.name,
            });
        }
    }, [amenityCategory, form]);

    const getMainImage = (): UpdateFileRequestDto | null => {
        return mainImagesDetails
            ? ({
                  delete: mainImagesDetails[0].isDeleted,
                  uploadBase64: mainImagesDetails[0].base64,
              } as UpdateFileRequestDto)
            : null;
    };

    const errorsHandler = (error: any): void => {
        let treated = false;
        const errors = new Map<string, string[]>();
        const responseErrors = error.response?.data?.errors;
        if (responseErrors && responseErrors['image.UploadBase64'] !== undefined) {
            treated = true;
            errors.set('image', [error.response?.data.errors['image.UploadBase64'][0].description]);
        }
        setErrors(errors);

        if (!treated) {
            toastStore.displayError(error);
        }
    };

    const createAmenityCategory = async (values: Store): Promise<void> => {
        const data: CreateAmenityCategoryRequestDto = {
            name: values.name,
            image: getMainImage(),
        };

        if (!(await validateForm(data, false))) return;

        try {
            globalLoadingStore.addLoading();
            await amenityCategoryService.createAmenityCategory(data);
            globalLoadingStore.removeLoading();
            await success();
        } catch (e) {
            errorsHandler(e);
        } finally {
            globalLoadingStore.removeLoading();
        }
    };

    const editAmenityCategory = async (values: Store): Promise<void> => {
        const oldValues = amenityCategory as UpdateAmenityCategoryRequestDto;
        const data: UpdateAmenityCategoryRequestDto = {
            name: values.name || oldValues.name,
            image: getMainImage(),
        };

        if (!(await validateForm(data, false))) return;

        try {
            globalLoadingStore.addLoading();
            await amenityCategoryService.updateAmenityCategory(data, amenityCategory!.id as string);
            globalLoadingStore.removeLoading();
            await success();
        } catch (e) {
            errorsHandler(e);
        } finally {
            globalLoadingStore.removeLoading();
        }
    };

    const submit = async (values: Store): Promise<void> => {
        if (amenityCategory !== undefined) {
            await editAmenityCategory(values);
        } else {
            await createAmenityCategory(values);
        }
    };

    const exit = async (): Promise<void> => {
        if (
            !(await confirmationModalStore.confirm({
                icon: <AmenityIcon />,
                title: t(`leave_confirm_title`),
                message: t(`leave_confirm_message`),
                positiveText: t(
                    `Food.category_leave_confirm_positive${
                        amenityCategory !== undefined ? '_edit' : ''
                    }`
                ),
                negativeText: t(`leave_confirm_negative`),
            }))
        )
            return;
        dismiss();
    };

    return (
        <Modal
            visible={visible}
            centered
            title={
                amenityCategory !== undefined
                    ? t('Amenities.amenity_categories_edit')
                    : t('Amenities.amenity_categories_create')
            }
            className="FormModal"
            closeIcon={<Close />}
            width={960}
            footer={null}
            onCancel={exit}
            maskClosable={false}
        >
            <div className="AmenityCategoryModal">
                <Form layout="vertical" onFinish={submit} form={form}>
                    <Row gutter={titleGutter}>
                        <Col span={24} className="formSection">
                            <Title level={4}>{t('basic_information')}</Title>
                        </Col>
                    </Row>
                    <Row gutter={formGutter}>
                        <Col span={24}>
                            <ValidatedFormItem
                                errors={errors}
                                name="name"
                                label={t('name')}
                                required={true}
                            >
                                <Input
                                    onChange={(e): void =>
                                        form.setFieldsValue({ name: e.target.value })
                                    }
                                />
                            </ValidatedFormItem>
                        </Col>
                    </Row>

                    <Row gutter={titleGutter}>
                        <Col span={24} className="formSection">
                            <Title level={4}>{t('Images.images_main')}</Title>
                        </Col>
                    </Row>

                    <Row gutter={formGutter} className="imageRow">
                        <Col span={24}>
                            <ValidatedFormItem errors={errors} name="image">
                                <ImagePicker
                                    images={
                                        amenityCategory?.imageUrl
                                            ? [
                                                  {
                                                      url: amenityCategory?.imageUrl,
                                                      uid: 'main',
                                                  } as UploadFile,
                                              ]
                                            : undefined
                                    }
                                    setImagesDetails={(images: ImageDetails[] | undefined) =>
                                        setMainImagesDetails(images)
                                    }
                                />
                            </ValidatedFormItem>
                        </Col>
                    </Row>

                    <div className="actions">
                        <Button
                            type="default"
                            className="secondary negative"
                            htmlType="button"
                            onClick={(): Promise<void> => exit()}
                        >
                            {t('cancel')}
                        </Button>
                        <Button type="primary" className="positive" htmlType="submit">
                            {t('save')}
                        </Button>
                    </div>
                </Form>
            </div>
        </Modal>
    );
    //#endregion
};

export default AmenityCategoryModal;
